import { useEffect, useMemo, useState } from 'react';

import { useMediaQuery } from '@/components/hooks/useMediaQuery';
import {
  MaintenanceListSearchFilterOption,
  MaintenanceListSearchFilterOptionItem,
  MaintenanceListSearchFilterSelectOptionData,
} from '@/components/sections/service/maintenance-list/search-filter';
import { MaintenanceListTableDisplay } from '@/components/sections/service/maintenance-list/table';
import {
  CONTENT_FILTER_SELECTS as CV_CONTENT_FILTER_SELECTS,
  CONTENTSTACK_CV_TYPE_TO_CUSTOM_NAME_TYPE,
  CUSTOM_CV_MODEL_IMAGE_MAP,
  DEFAULT_CV_VEHICLE_IMAGE,
} from '@/const/service/maintenance-list/cv';
import {
  CONTENT_FILTER_SELECTS as LCV_CONTENT_FILTER_SELECTS,
  CUSTOM_LCV_MODEL_IMAGE_MAP,
  CustomMaintenanceLcvModelImageData,
  DEFAULT_LCV_VEHICLE_IMAGE,
} from '@/const/service/maintenance-list/lcv';
import {
  getFirstSelectOptions,
  getFourthSelectOptions,
  getParsedData,
  getSecondSelectOptions,
  getThirdSelectOptions,
} from '@/contents/useMaintenanceList';
import {
  getCvFirstOptionLabel,
  getCvFirstOptionUniqueKey,
  getCvSecondOptionLabel,
  getCvSecondOptionUniqueKey,
  getCvServiceMaintenancePageUrlFormat,
} from '@/contents/useMaintenanceList/cv';
import {
  getLcvFirstOptionLabel,
  getLcvSecondOptionLabel,
  getLcvServiceMaintenancePageUrlFormat,
} from '@/contents/useMaintenanceList/lcv';
import { config } from '@/stitches.config';
import {
  trackMaintenanceSearchCv,
  trackMaintenanceSearchLcv,
} from '@/tag-manager';
import { IVehicleType } from '@/types/services/contentstack';
import type {
  ContentstackAllVehicleTypeMaintenanceOptionResponse,
  ContentstackCvMaintenanceOptionResponse,
  ContentstackLcvMaintenanceOptionResponse,
  MaintenanceOptionGroupedItem,
} from '@/types/services/contentstack/services/maintenance-service';

interface UseMaintenanceListProps {
  vehicleType: IVehicleType;
  url?: string;
  initialSelectValues: string[];
  preFetchedFirstOptions: MaintenanceOptionGroupedItem[];
  onSearch: (
    item?: MaintenanceListSearchFilterOptionItem,
    selectOptionValues?: string[],
    schedule?: string
  ) => void;
  onSelectOption?: (updateIndex: number, updateValue: string) => void;
}

interface UseMaintenanceList {
  vehicleImageSrc: string;
  selectedItem?: ContentstackAllVehicleTypeMaintenanceOptionResponse;
  selectOptionValues: string[];
  orderedFilterOptions: MaintenanceListSearchFilterSelectOptionData[];
  tableHeaderTitle: string;
  tableDisplay?: MaintenanceListTableDisplay;
  setSelectOption: (updateIndex: number, updateValue: string) => void;
  setTableDisplay: (status: MaintenanceListTableDisplay) => void;
}

export const useMaintenanceList = ({
  vehicleType,
  url,
  initialSelectValues,
  preFetchedFirstOptions,
  onSearch,
  onSelectOption,
}: UseMaintenanceListProps): UseMaintenanceList => {
  const isDesktop = useMediaQuery(config.media.xl);
  const [selectOptionValues, setSelectOptionValues] =
    useState<string[]>(initialSelectValues);
  const [tableDisplay, setTableDisplay] = useState<
    MaintenanceListTableDisplay | undefined
  >(undefined);

  const firstSelectOptions = getFirstSelectOptions(
    preFetchedFirstOptions,
    vehicleType
  );

  const selectFirstItem = useMemo<
    MaintenanceListSearchFilterOption | undefined
  >(
    () =>
      firstSelectOptions.find((item) => item.value === selectOptionValues[0]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectOptionValues[0]]
  );

  const secondSelectOptions = useMemo<MaintenanceListSearchFilterOption[]>(
    () => getSecondSelectOptions(selectFirstItem, vehicleType),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectFirstItem]
  );

  const selectSecondItem = useMemo<
    MaintenanceListSearchFilterOption | undefined
  >(
    () =>
      secondSelectOptions.find((item) => item.value === selectOptionValues[1]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectOptionValues[1]]
  );

  const thirdSelectOptions = useMemo<MaintenanceListSearchFilterOption[]>(
    () => getThirdSelectOptions(selectSecondItem, vehicleType),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectSecondItem]
  );

  const selectThirdItem = useMemo<
    MaintenanceListSearchFilterOption | undefined
  >(
    () =>
      thirdSelectOptions.find((item) => item.value === selectOptionValues[2]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectOptionValues[2]]
  );

  const fourthSelectOptions = useMemo<MaintenanceListSearchFilterOption[]>(
    () => getFourthSelectOptions(selectThirdItem, vehicleType),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectThirdItem]
  );

  const selectFourthItem = useMemo<
    MaintenanceListSearchFilterOption | undefined
  >(
    () =>
      fourthSelectOptions.find((item) => item.value === selectOptionValues[3]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectOptionValues[3]]
  );

  const firstFilterSelect =
    useMemo<MaintenanceListSearchFilterSelectOptionData>(
      () => ({
        key:
          vehicleType === 'lcv'
            ? LCV_CONTENT_FILTER_SELECTS[0].key
            : CV_CONTENT_FILTER_SELECTS[0].key,
        placeholder:
          vehicleType === 'lcv'
            ? LCV_CONTENT_FILTER_SELECTS[0].placeholder
            : CV_CONTENT_FILTER_SELECTS[0].placeholder,
        options: firstSelectOptions,
      }),

      [vehicleType, firstSelectOptions]
    );

  const secondFilterSelect =
    useMemo<MaintenanceListSearchFilterSelectOptionData>(
      () => ({
        key:
          vehicleType === 'lcv'
            ? LCV_CONTENT_FILTER_SELECTS[1].key
            : CV_CONTENT_FILTER_SELECTS[1].key,
        placeholder:
          vehicleType === 'lcv'
            ? LCV_CONTENT_FILTER_SELECTS[1].placeholder
            : CV_CONTENT_FILTER_SELECTS[1].placeholder,
        options: secondSelectOptions,
      }),

      [vehicleType, secondSelectOptions]
    );

  const thirdFilterSelect =
    useMemo<MaintenanceListSearchFilterSelectOptionData>(
      () => ({
        key:
          vehicleType === 'lcv'
            ? LCV_CONTENT_FILTER_SELECTS[2].key
            : CV_CONTENT_FILTER_SELECTS[2].key,
        placeholder:
          vehicleType === 'lcv'
            ? LCV_CONTENT_FILTER_SELECTS[2].placeholder
            : CV_CONTENT_FILTER_SELECTS[2].placeholder,
        options: thirdSelectOptions,
      }),

      [vehicleType, thirdSelectOptions]
    );

  const fourthFilterSelect =
    useMemo<MaintenanceListSearchFilterSelectOptionData>(
      () => ({
        key: vehicleType === 'lcv' ? '' : CV_CONTENT_FILTER_SELECTS[3].key,
        placeholder:
          vehicleType === 'lcv' ? '' : CV_CONTENT_FILTER_SELECTS[3].placeholder,
        options: fourthSelectOptions,
      }),

      [vehicleType, fourthSelectOptions]
    );

  const tableHeaderTitle = useMemo(() => {
    if (!selectThirdItem || !selectThirdItem.item) return '';

    if (vehicleType === 'lcv')
      return `${getLcvFirstOptionLabel(
        selectThirdItem.item
      )} ${getLcvSecondOptionLabel(selectThirdItem.item)}`;

    return `${selectThirdItem.item?.vehicle_name} เครื่องยนต์ รุ่น ${selectThirdItem.item?.engine_model}`;
  }, [vehicleType, selectThirdItem]);

  const setSelectOption = (updateIndex: number, updateValue: string) => {
    onSelectOption && onSelectOption(updateIndex, updateValue);
    const updateResults = selectOptionValues.map(
      (selectValue, selectValueIndex) => {
        if (updateIndex === selectValueIndex) return updateValue;
        return updateIndex < selectValueIndex ? '' : selectValue;
      }
    );
    setSelectOptionValues(updateResults);
  };

  // For LCV search
  useEffect(() => {
    if (!selectThirdItem || !selectThirdItem.item || vehicleType === 'cv')
      return;

    onSearch(selectThirdItem.item, selectOptionValues);
    trackMaintenanceSearchLcv({
      model: getLcvFirstOptionLabel(selectThirdItem.item),
      engine: getLcvSecondOptionLabel(selectThirdItem.item),
      mileage: `${selectThirdItem.item?.mileage}`,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectThirdItem, vehicleType]);

  // For CV search
  useEffect(() => {
    if (!selectFourthItem || !selectFourthItem.item || vehicleType === 'lcv')
      return;

    onSearch(selectFourthItem.item, selectOptionValues);
    trackMaintenanceSearchCv({
      category: getCvFirstOptionLabel(selectThirdItem?.item) ?? '',
      model: getCvSecondOptionLabel(selectThirdItem?.item) ?? '',
      year: selectThirdItem?.label ?? '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectFourthItem, vehicleType]);

  useEffect(() => {
    if (selectOptionValues[2]) return;
    if (secondSelectOptions.length === 1) {
      setSelectOption(1, secondSelectOptions[0].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondSelectOptions]);

  useEffect(() => {
    if (!thirdSelectOptions[0]) return;

    if (thirdSelectOptions.length === 1) {
      setSelectOption(2, thirdSelectOptions[0].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleType, thirdSelectOptions]);

  useEffect(() => {
    setTableDisplay(isDesktop ? 'full' : 'brief');
  }, [isDesktop]);

  const getLcvVehicleImageByUrl = (url: string) => {
    const foundedItem = thirdSelectOptions.find(
      (optionItem) =>
        url ===
        getLcvServiceMaintenancePageUrlFormat(
          optionItem.item as ContentstackLcvMaintenanceOptionResponse
        )
    );
    if (!foundedItem) return DEFAULT_LCV_VEHICLE_IMAGE;

    const item = foundedItem.item;
    const vehicleBodyType = getParsedData(`${item?.vehicle_body_type}`);
    const vehicleBodyTypeYearListData: CustomMaintenanceLcvModelImageData =
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      CUSTOM_LCV_MODEL_IMAGE_MAP[vehicleBodyType];
    const vehicleStartYear = getParsedData(`${item?.model_year_begin}`);

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const imageData = vehicleBodyTypeYearListData[vehicleStartYear];

    if (!imageData) return DEFAULT_LCV_VEHICLE_IMAGE;

    if (imageData.isMuX) {
      const founedGrade = getParsedData(`${item?.class_one_marketing_grade}`);
      return imageData.grades[founedGrade] ?? DEFAULT_LCV_VEHICLE_IMAGE;
    }

    if (imageData.engines) {
      const isMhev = (item?.title || '').toLowerCase().includes('mhev');
      return (
        imageData.engines[
          isMhev ? '19mhev' : getParsedData(`${item?.engine_size}`)
        ] ?? DEFAULT_LCV_VEHICLE_IMAGE
      );
    }
    return imageData.url;
  };

  const getCvVehicleImageByUrl = (url: string) => {
    const foundedItem = thirdSelectOptions.find(
      (optionItem) =>
        url === getCvServiceMaintenancePageUrlFormat(optionItem.item)
    );

    if (!foundedItem) return DEFAULT_CV_VEHICLE_IMAGE;

    const item = foundedItem.item;
    const model = `${
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      CONTENTSTACK_CV_TYPE_TO_CUSTOM_NAME_TYPE[getCvFirstOptionUniqueKey(item)]
    }_${getCvSecondOptionUniqueKey(
      item as ContentstackCvMaintenanceOptionResponse
    )}`;

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return CUSTOM_CV_MODEL_IMAGE_MAP[model] ?? DEFAULT_CV_VEHICLE_IMAGE;
  };

  const vehicleImageSrc = useMemo(() => {
    if (!url)
      return vehicleType === 'lcv'
        ? DEFAULT_LCV_VEHICLE_IMAGE
        : DEFAULT_CV_VEHICLE_IMAGE;

    return vehicleType === 'lcv'
      ? getLcvVehicleImageByUrl(url)
      : getCvVehicleImageByUrl(url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleType, url]);

  return {
    vehicleImageSrc,
    selectedItem: selectThirdItem?.item,
    selectOptionValues,
    orderedFilterOptions:
      vehicleType === 'lcv'
        ? [firstFilterSelect, secondFilterSelect, thirdFilterSelect]
        : [
            firstFilterSelect,
            secondFilterSelect,
            thirdFilterSelect,
            fourthFilterSelect,
          ],
    tableHeaderTitle,
    tableDisplay,
    setSelectOption,
    setTableDisplay,
  };
};
